import * as R from 'ramda';
import { Plover } from '@blisspointmedia/bpm-types/dist/webTrafficOptimization';
import { convert24hrTo12hr } from '../../utils/time-utils';
import {
  defaultCustomRetention,
  defaultTimeframe,
  WtoCustomRetention,
  WtoDowOperation,
  WtoRule,
  WtoTimeframe
} from './wto-form-context';

export const getParamFunnelStep = (utmParam: string): string =>
  utmParam.length > 0 ? utmParam.split('utm_')[1].split('_')[0] : '';

export const getParamTestName = (utmParam: string): string =>
  utmParam.length > 0 ? utmParam.split('=')[1].split(/[0-9]/)[0] : '';

// Only the version digits are captured; any trailing custom-identifier suffix is dropped.
// TODO(DEV-24309): add a dedicated suffix input/getter/setter and relax the utm_test_id
// validation regexes (validate-basic-route-fields.ts, validate-timeframe.ts) to allow it.
export const getParamVersionNumber = (utmParam: string): string => {
  const match = utmParam.match(/^[^0-9]+/);
  if (!match) {
    return '';
  }
  const versionMatch = utmParam.slice(match[0].length).match(/^[0-9]+/);
  return versionMatch ? versionMatch[0] : '';
};

export const updateParamFunnelStep = (funnelStep: string, testId: string): string =>
  `utm_${funnelStep.toLocaleLowerCase()}_exp=${getParamTestName(testId)}${getParamVersionNumber(
    testId
  )}`;

export const updateParamTestName = (testName: string, testId: string): string =>
  `utm_${getParamFunnelStep(testId)}_exp=${testName}${getParamVersionNumber(testId)}`;

export const updateParamVersionNumber = (versionNumber: number, testId: string): string =>
  `utm_${getParamFunnelStep(testId)}_exp=${getParamTestName(testId)}${
    versionNumber < 1 ? 1 : versionNumber
  }`;

export const formatDefaultId = (id: string): string =>
  id ? `${id?.toString()?.indexOf('f-') === -1 ? 'f-' : ''}${id}` : '';

export const prepPayloadToCreateTest = (
  configObj: Partial<Plover>,
  currentPartnerId: string,
  userEmail: string
) => ({
  created_by: userEmail || '',
  test_name: configObj.test_name ? configObj.test_name : '',
  utm_test_id: configObj.utm_test_id ? configObj.utm_test_id : '',
  variants: configObj.variants ? configObj.variants : '',
  default_id: formatDefaultId(configObj.default_id || ''),
  default_url: configObj.default_url ? configObj.default_url : '',
  plover_url: configObj.plover_url ? configObj.plover_url : '',
  folder_path: configObj.folder_path?.toString()?.replace(/^\/|\/$/g, ''),
  notes: configObj.notes ? configObj.notes : '',
  domain: configObj.domain ? configObj.domain : '',
  rules: [],
  status: 'draft',
  company: currentPartnerId || '',
  updated_by: userEmail || ''
});

export const isSavedDraft = (data: Plover): boolean =>
  Boolean(data.id) && data.status.toLocaleLowerCase() === 'draft';

const EDITABLE_FIELDS = [
  'test_name',
  'domain',
  'folder_path',
  'plover_url',
  'utm_test_id',
  'default_id',
  'default_url',
  'notes'
];

export const deriveFormEdited = (wtoFormObj: Record<string, string>): boolean =>
  EDITABLE_FIELDS.some(field => {
    if (!wtoFormObj[field] || !wtoFormObj[field].length) return false;
    return field === 'folder_path' ? wtoFormObj.folder_path !== '/' : true;
  });

export const buildPloverUrl = (domain: string, folderPath: string, ploverUrl: string): string => {
  const url = `https://${domain || ''}${domain ? folderPath || '/' : ''}${
    domain ? ploverUrl || '' : ''
  }`;
  return url;
};

export const trimBackslashes = (path: string): string => {
  const withoutLeading = path.length >= 1 && path.charAt(0) === '/' ? path.substring(1) : path;
  return withoutLeading.length >= 1 && withoutLeading.charAt(withoutLeading.length - 1) === '/'
    ? withoutLeading.substring(0, withoutLeading.length - 1)
    : withoutLeading;
};

export const joinBackslashes = (path: string): string => {
  if (path === '') return '/';
  const withLeading = path.charAt(0) !== '/' ? `/${path}` : path;
  return withLeading.length > 1 && withLeading.charAt(withLeading.length - 1) !== '/'
    ? `${withLeading}/`
    : withLeading;
};

export const formatRules = (
  rules: WtoRule[],
  removeTimeframeTrackingParam?: boolean
): Record<string, any>[] => {
  // The API expects conditions as legacy 4-tuples ([trigger, rule, value, operator]), not the
  // {trigger, rule, value, operator} object shape WtoRule uses — so the output diverges from
  // WtoRule and stays a plain Record.
  const formattedRules: Record<string, any>[] = R.clone(rules);
  rules.forEach((rule, ruleIndex) => {
    const formattedRule = R.clone(rule) as Record<string, any>;
    if (rule.conditions) {
      rule.conditions.forEach((condition, conditionIndex) => {
        const formattedCondition: string[] = [];
        formattedCondition.push(condition.trigger);
        formattedCondition.push(condition.rule);
        formattedCondition.push(condition.value);
        formattedCondition.push(condition.operator);
        formattedRule.conditions[conditionIndex] = formattedCondition;
      });
    }
    if (removeTimeframeTrackingParam && rule.timeframe && rule.timeframe_enabled) {
      const formattedTimeframe = R.clone(rule.timeframe);
      formattedTimeframe.timeframe_utm_test_id_enabled = false;
      formattedTimeframe.utm_test_id = '';
      formattedRule.timeframe = formattedTimeframe;
    }
    if (rule.customRetention && rule.customRetention.customRetentionStatus) {
      const formattedRetention = R.clone(rule.customRetention);
      formattedRetention.startTime = rule.customRetention.startDate;
      formattedRetention.formattedStartTime = rule.customRetention.formattedStartDate;
      formattedRule.customRetention = formattedRetention;
    }
    formattedRules[ruleIndex] = formattedRule;
  });
  return formattedRules;
};

export const parseTimestampComponents = (
  data: Record<string, any>,
  prop: string,
  defaultDate: string
): {
  year: any;
  month: any;
  day: any;
  hour24: any;
  hour12: string;
  period: string;
  minute: any;
  offset: string;
  timezone: any;
} => {
  const [defaultYear, defaultMonth, defaultDay] = defaultDate.split('-').map(token => token);
  const { time_zone } = data;
  const isEmpty = !data[prop];
  const getPeriod = (): string => {
    if (isEmpty) return 'am';
    return Number(data[prop].substring(11, 13)) > 11 ? 'pm' : 'am';
  };

  return {
    year: isEmpty ? defaultYear : data[prop].substring(0, 4),
    month: isEmpty ? defaultMonth : data[prop].substring(5, 7),
    day: isEmpty ? defaultDay : data[prop].substring(8, 10),
    hour24: isEmpty ? '00' : data[prop].substring(11, 13),
    hour12: isEmpty ? '12' : convert24hrTo12hr(data[prop].substring(11, 16)).split(':')[0],
    period: getPeriod(),
    minute: isEmpty ? '00' : data[prop].substring(14, 16),
    offset: isEmpty ? '+00:00' : `${data[prop].substring(19, 20)}${data[prop].substring(20, 25)}`,
    timezone: time_zone || 'UTC'
  };
};

export const capitalize = (str: string): string => `${str.charAt(0).toUpperCase()}${str.slice(1)}`;

export const parseTimestampTimeframe = (
  defaultDate: string,
  data?: WtoTimeframe
): {
  start: {
    year: any;
    month: any;
    day: any;
    hour24: any;
    hour12: string;
    period: string;
    minute: any;
    offset: string;
    timezone: any;
  };
  end: {
    year: any;
    month: any;
    day: any;
    hour24: any;
    hour12: string;
    period: string;
    minute: any;
    offset: string;
    timezone: any;
  };
} => {
  const timeframe = data || defaultTimeframe;

  return {
    start: parseTimestampComponents(timeframe, 'startDateTime', defaultDate),
    end: parseTimestampComponents(timeframe, 'endDateTime', defaultDate)
  };
};

export const parseTimestampDowOperations = (
  defaultDate: string,
  data?: WtoDowOperation[]
): {
  start: {
    year: any;
    month: any;
    day: any;
    hour24: any;
    hour12: string;
    period: string;
    minute: any;
    offset: string;
    timezone: any;
  };
  end: {
    year: any;
    month: any;
    day: any;
    hour24: any;
    hour12: string;
    period: string;
    minute: any;
    offset: string;
    timezone: any;
  };
}[] =>
  (data || []).map(operation => ({
    start: parseTimestampComponents(operation, 'start_time', defaultDate),
    end: parseTimestampComponents(operation, 'end_time', defaultDate)
  }));

export const parseTimestampCustomRetention = (
  defaultDate: string,
  data?: WtoCustomRetention
): {
  year: any;
  month: any;
  day: any;
  hour24: any;
  hour12: string;
  period: string;
  minute: any;
  offset: string;
  timezone: any;
} => {
  const retention = !data || Object.keys(data).length === 0 ? defaultCustomRetention : data;
  return parseTimestampComponents(retention, 'startDate', defaultDate);
};

export const setTimestampTime = (values: {
  year: any;
  month: any;
  day: any;
  hour24: any;
  hour12: string;
  period: string;
  minute: any;
  offset: string;
  timezone: any;
}): string =>
  `${values.year}-${values.month}-${values.day}T${values.hour24}:${values.minute}:00${values.offset}`;

export const setFormattedTime = (values: {
  year: any;
  month: any;
  day: any;
  hour24: any;
  hour12: string;
  period: string;
  minute: any;
  offset: string;
  timezone: any;
}): string =>
  `${values.year}/${Number(values.month)}/${Number(values.day)}, ${values.hour12}:${values.minute}${
    values.period
  }`;

export const formatTimestampOffset = (offset: number): string =>
  `${offset < 0 ? '-' : '+'}${Math.abs(Math.floor(offset)).toString().padStart(2, '0')}:00`;
